/**
 * AVBP — Lead ↔ Conversation IDENTITY JOIN (AD-039, Phase 1a of the
 * leads/quotes streamline, 2026-07-03).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The ONE rule for "does this inbound conversation belong to this lead?":
 *  an EXACT identity match only —
 *    • phone: both sides normalised through `contactMatch.phoneKey` (the
 *      AD-039 Stage-0 canonical last-10-digit key, tolerant of +91 / 091 /
 *      leading-0 / spaces / dashes / brackets), and
 *    • email: both sides lower-cased + trimmed (`contactMatch.emailKey`).
 *  Nothing weaker joins. Name similarity NEVER joins (owner-locked AD-039
 *  tuning: a false merge is worse than a miss). Non-exact senders keep the
 *  manual triage flow untouched (AD-015/050 — no auto-lead creation from
 *  unknown numbers; this module never creates anything).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  COMPUTED, NOT PERSISTED (v1) — why, exactly
 * ─────────────────────────────────────────────────────────────────────────
 *  The brief asked for the join to be STORED both ways. Both persistence
 *  halves are blocked by the locked security model, so v1 is a COMPUTED
 *  join (which is fully deterministic — the join key IS the phone/email):
 *    • thread → leadId: conversation rows live on the SERVER-ONLY
 *      `wa_conversations` index (firestore.rules default-deny; the portal
 *      reads it only through the `waListConversations` callable — see
 *      lib/messagesStore.ts header). No client write path exists.
 *    • lead → thread ref: the `leads/` rules whitelist blocks new top-level
 *      fields; extra Lead fields ride inside the `notes_internal` snapshot,
 *      which requires a `Lead` model field in lib/leads.ts — owned by
 *      another lane right now (reported, not edited here).
 *  The webhook-side join (the WA router stamping leadId on the conversation
 *  index at inbound time) is the correct durable home — a later phase in
 *  the functions repo.
 *
 *  PURE except the one marked fetch seam at the bottom. No React. The Node
 *  test runner exercises the join contract directly (test/leadThreadJoin
 *  .test.cjs) with the real-world number shapes (+91 78382 22671 ·
 *  7838222671 · 917838222671 must all join; a different number never).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { emailKey, phoneKey } from './contactMatch';
import { isTerminal, LEAD_STATE_LABEL, type Lead } from './leads';
import type { ConversationRow } from './messagesStore';

/* ════════════════════ the join rule (pure) ════════════════════ */

/**
 * Minimum digits for a phone join. `phoneKey` canonicalises to the LAST 10
 * digits (India-standard full subscriber number); anything shorter is a
 * fragment and must never join (a 4-digit tail matching is not identity).
 */
const MIN_PHONE_KEY_LEN = 10;

/** PURE — exact-phone join key; '' when the input can't form a full key. */
export function joinPhoneKey(p: string | null | undefined): string {
  const k = phoneKey(p ?? '');
  return k.length >= MIN_PHONE_KEY_LEN ? k : '';
}

/** PURE — exact-email join key ('' when absent). */
export function joinEmailKey(e: string | null | undefined): string {
  return emailKey(e ?? '');
}

/**
 * PURE — does this lead join this conversation identity? EXACT phone OR
 * EXACT email, nothing weaker. Trashed leads never join (they are hidden
 * from every surface).
 */
export function leadJoinsIdentity(
  lead: Lead,
  phone: string | null | undefined,
  email: string | null | undefined,
): boolean {
  if (lead.trashed === true) return false;
  const pk = joinPhoneKey(phone);
  if (pk && joinPhoneKey(lead.phone) === pk) return true;
  const ek = joinEmailKey(email);
  if (ek && joinEmailKey(lead.email) === ek) return true;
  return false;
}

/**
 * PURE — resolve the ONE lead an inbound conversation identity joins to.
 * Exact matches only; when the same person has several leads (same exact
 * phone), prefer the ACTIVE one (non-terminal state) and then the most
 * recently updated — deterministic, so the chip never flickers between
 * leads across polls. Returns null when nothing matches exactly.
 */
export function findLeadForThread(
  phone: string | null | undefined,
  email: string | null | undefined,
  leads: Lead[],
): Lead | null {
  const matches = (leads || []).filter((l) =>
    leadJoinsIdentity(l, phone, email),
  );
  if (matches.length === 0) return null;
  const sorted = [...matches].sort((a, b) => {
    const at = isTerminal(a.state) ? 1 : 0;
    const bt = isTerminal(b.state) ? 1 : 0;
    if (at !== bt) return at - bt; // active spine first
    const au = Date.parse(a.updatedAt) || 0;
    const bu = Date.parse(b.updatedAt) || 0;
    if (au !== bu) return bu - au; // newest activity first
    return a.id.localeCompare(b.id); // stable tie-break
  });
  return sorted[0];
}

/* ════════════════════ chip view-model (pure) ════════════════════ */

/** What the Team-Inbox thread-header lead chip renders. */
export interface LinkedLeadChip {
  /** leads/{id} — the deep-link target (/leads/:id). */
  id: string;
  name: string;
  /** Stage wording from LEAD_STATE_LABEL — the ONE existing label source. */
  stageLabel: string;
}

/** PURE — the chip model for a joined lead (stage wording never invented). */
export function leadChipModel(lead: Lead): LinkedLeadChip {
  return {
    id: lead.id,
    name: lead.name,
    stageLabel: LEAD_STATE_LABEL[lead.state],
  };
}

/* ════════════════════ lead → thread lookup (pure core) ════════════════════ */

/** The compact thread reference the lead record's Conversation block shows. */
export interface LeadThreadRef {
  /** Conversation key — deep-link `/messages?c=<key>` (both the Team Inbox
   *  and the legacy Messages screen honour `?c=`). */
  key: string;
  channel: 'whatsapp';
  /** Last-message snippet from the conversation index (plain text). */
  lastMessage: string;
  /** Epoch ms of the last message; null for rows that predate the field. */
  lastMessageAt: number | null;
}

/**
 * PURE — find the lead's conversation among the listed rows. WhatsApp rows
 * only (email triage items are records, not threads); AI-agent/internal
 * threads (`kind:'agent'`) never join a lead. Exact phone key match; when
 * several rows match (e.g. a triage row + a later client row for the same
 * number), the most recently active one wins.
 */
export function findThreadForLead(
  lead: Pick<Lead, 'phone' | 'trashed'>,
  rows: ConversationRow[],
): ConversationRow | null {
  const pk = joinPhoneKey(lead.phone);
  if (!pk || lead.trashed === true) return null;
  let best: ConversationRow | null = null;
  for (const r of rows || []) {
    if (r.kind === 'agent') continue;
    if (joinPhoneKey(r.phone) !== pk) continue;
    if (!best || (r.lastMessageAt || 0) > (best.lastMessageAt || 0)) {
      best = r;
    }
  }
  return best;
}

/** PURE — map a matched row to the compact block the lead record renders. */
export function toLeadThreadRef(row: ConversationRow): LeadThreadRef {
  return {
    key: row.key,
    channel: 'whatsapp',
    lastMessage: row.lastMessage || '',
    lastMessageAt: row.lastMessageAt ?? null,
  };
}

/* ════════════ stored-key resolution (Phase-1 integration, 2026-07-03) ════════════
 *
 * The Lead model now carries `waThreadKey` (+ `threadLinkSource`) as snapshot
 * fields, so the join is STICKY: prefer the stored key; fall back to a fresh
 * computed exact-phone match when the stored thread vanished. The resolver
 * reports HOW it matched so the caller knows when a computed match is a
 * candidate for persisting onto the lead (via the existing updateLead seam).
 */

export interface LeadThreadLookup {
  ref: LeadThreadRef | null;
  /**
   * 'stored'   — the lead's persisted `waThreadKey` resolved to a live row;
   * 'computed' — a fresh exact-phone match (candidate to persist);
   * null       — nothing joined.
   */
  via: 'stored' | 'computed' | null;
}

/**
 * PURE — resolve the lead's conversation among the listed rows, preferring
 * the STORED `waThreadKey` and falling back to the computed exact-phone
 * match (same rule as `findThreadForLead`). Agent threads never resolve,
 * stored or not; a trashed lead never resolves.
 */
export function resolveThreadForLead(
  lead: Pick<Lead, 'phone' | 'trashed'> & { waThreadKey?: string | null },
  rows: ConversationRow[],
): LeadThreadLookup {
  if (lead.trashed === true) return { ref: null, via: null };
  if (lead.waThreadKey) {
    const stored = (rows || []).find(
      (r) => r.key === lead.waThreadKey && r.kind !== 'agent',
    );
    if (stored) return { ref: toLeadThreadRef(stored), via: 'stored' };
    // stored thread vanished → fall through to the computed match
  }
  const computed = findThreadForLead(lead, rows);
  return computed
    ? { ref: toLeadThreadRef(computed), via: 'computed' }
    : { ref: null, via: null };
}

/* ════════════════════ fetch seam (impure — the ONE side effect) ════════════════════ */

/**
 * Look up a lead's conversation via the EXISTING `waListConversations`
 * callable (the only sanctioned read of the server-only conversation
 * index — same transport the Team Inbox polls). Best-effort: any failure
 * (offline / role-rejected / cold function) resolves to null so the lead
 * record renders its quiet "no conversation yet" line instead of erroring.
 * Team roles only — the caller must gate on the team view (the callable
 * rejects clients server-side anyway; this avoids the error round-trip).
 */
export async function fetchThreadRefForLead(
  lead: Pick<Lead, 'phone' | 'trashed'>,
): Promise<LeadThreadRef | null> {
  if (!joinPhoneKey(lead.phone) || lead.trashed === true) return null;
  try {
    const { fetchConversations } = await import('./messagesStore');
    const res = await fetchConversations();
    const row = findThreadForLead(lead, res.conversations || []);
    return row ? toLeadThreadRef(row) : null;
  } catch {
    return null;
  }
}

/**
 * Stored-key-aware lookup for the lead record (Phase-1 integration). Same
 * transport + best-effort behaviour as `fetchThreadRefForLead`, but resolves
 * through `resolveThreadForLead` and reports the match origin so the caller
 * can persist a fresh computed join onto the lead.
 */
export async function fetchThreadLookupForLead(
  lead: Pick<Lead, 'phone' | 'trashed'> & { waThreadKey?: string | null },
): Promise<LeadThreadLookup> {
  if (lead.trashed === true) return { ref: null, via: null };
  if (!lead.waThreadKey && !joinPhoneKey(lead.phone)) {
    return { ref: null, via: null };
  }
  try {
    const { fetchConversations } = await import('./messagesStore');
    const res = await fetchConversations();
    return resolveThreadForLead(lead, res.conversations || []);
  } catch {
    return { ref: null, via: null };
  }
}
